import { useState } from "react";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { Badge } from "components/ui/badge";
import {
  Plus,
  CaretDown,
  CaretRight,
  Calculator,
  ListNumbers,
} from "@phosphor-icons/react";
import { ExtractionRuleEditor, AIFallbackEditor } from "./ExtractionRuleEditor";
import {
  FieldExtractionRule,
  StandardFieldExtraction,
  AIFallbackConfig,
} from "./types";

interface FieldExtractionRulesPanelProps {
  fieldConfig: StandardFieldExtraction;
  onUpdate: (config: Partial<StandardFieldExtraction>) => void;
  fieldName: string; // Used for display purposes
}

// Generate a unique ID
function generateId(): string {
  return `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * FieldExtractionRulesPanel - Panel for configuring extraction rules for a single field
 */
export const FieldExtractionRulesPanel = ({
  fieldConfig,
  onUpdate,
  fieldName: _fieldName, // Used for future display purposes
}: FieldExtractionRulesPanelProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const rules = fieldConfig.extractionRules || [];
  const aiFallback = fieldConfig.aiFallback;

  const addRule = () => {
    const newRule: FieldExtractionRule = {
      id: generateId(),
      name: '',
      priority: rules.length,
      calculationRule: {
        baseField: 'documentDate',
        operation: 'add',
        amount: 10,
        unit: 'years',
      },
    };
    onUpdate({
      extractionRules: [...rules, newRule],
    });
    setIsExpanded(true);
  };

  const updateRule = (index: number, updatedRule: FieldExtractionRule) => {
    const newRules = [...rules];
    newRules[index] = updatedRule;
    onUpdate({ extractionRules: newRules });
  };

  const deleteRule = (index: number) => {
    const newRules = rules.filter((_, i) => i !== index);
    onUpdate({ extractionRules: newRules });
  };

  const moveRule = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= rules.length) return;

    const newRules = [...rules];
    [newRules[index], newRules[newIndex]] = [newRules[newIndex], newRules[index]];
    // Update priorities
    newRules.forEach((rule, i) => {
      rule.priority = i;
    });
    onUpdate({ extractionRules: newRules });
  };

  const updateAiFallback = (config: AIFallbackConfig | undefined) => {
    onUpdate({ aiFallback: config });
  };

  const hasRules = rules.length > 0;
  const hasAiFallback = aiFallback?.enabled ?? false;

  return (
    <div>
      {/* Header/Trigger */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-2 -mx-2 rounded hover:bg-neutral-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <CaretDown className="h-4 w-4 text-neutral-400" />
          ) : (
            <CaretRight className="h-4 w-4 text-neutral-400" />
          )}
          <Calculator className="h-4 w-4 text-emerald-600" weight="duotone" />
          <span className="text-xs font-medium">Berekeningsregels</span>
        </div>
        <div className="flex items-center gap-1">
          {hasRules && (
            <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-700">
              {rules.length} regel{rules.length !== 1 ? 's' : ''}
            </Badge>
          )}
          {hasAiFallback && (
            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
              AI fallback
            </Badge>
          )}
          {!hasRules && !hasAiFallback && (
            <Badge variant="outline" className="text-xs">
              Niet geconfigureerd
            </Badge>
          )}
        </div>
      </button>

      {/* Content - only shown when expanded */}
      {isExpanded && (
        <div className="space-y-4 pt-2">
          {/* Info Text */}
          <div className="text-xs text-neutral-500 bg-emerald-50 p-2 rounded">
            <strong>Hoe werkt dit?</strong>
            <ul className="mt-1 space-y-0.5 list-disc pl-4">
              <li>Regels worden van boven naar beneden geëvalueerd</li>
              <li>De eerste regel waarvan de voorwaarden voldoen, wordt toegepast</li>
              <li>Als geen regel van toepassing is, wordt AI fallback gebruikt (indien ingeschakeld)</li>
              <li>Zonder regels of AI fallback wordt standaard AI extractie gebruikt</li>
            </ul>
          </div>

          {/* Rules List */}
          {hasRules && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <ListNumbers className="h-4 w-4 text-neutral-500" />
                <Label className="text-xs font-medium">Regels (prioriteit: boven naar beneden)</Label>
              </div>
              <div className="space-y-2">
                {rules.map((rule, index) => (
                  <ExtractionRuleEditor
                    key={rule.id || index}
                    rule={rule}
                    index={index}
                    totalRules={rules.length}
                    onUpdate={(updated) => updateRule(index, updated)}
                    onDelete={() => deleteRule(index)}
                    onMoveUp={() => moveRule(index, 'up')}
                    onMoveDown={() => moveRule(index, 'down')}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Add Rule Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={addRule}
            className="w-full h-8 text-xs border-dashed"
          >
            <Plus className="h-3 w-3 mr-1" />
            Berekeningsregel toevoegen
          </Button>

          {/* AI Fallback */}
          <div className="border-t pt-3">
            <AIFallbackEditor config={aiFallback} onChange={updateAiFallback} />
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldExtractionRulesPanel;
